import { useRef, useState } from "react"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
  ScrollArea,
} from "@ui/shad"
import { cn } from "@ui/utils"
import { Check, Loader2 } from "lucide-react"

import { useDebounce } from "@/hooks/use-debounce"

import { BaseInlineInput } from "../base"
import { InlineSelectProps } from "../props"
import { useInlineEdit } from "../use-inline-edit"

export function InlineSelect<T = string>({
  label,
  LabelIcon,
  hideLabel = false,
  size,
  placeholder = "Select an option",
  value: controlledValue,
  defaultValue,
  options,
  onChange,
  onSave,
  onSearch,
  isSaving = false,
  isSearching = false,
  disabled = false,
  clearable = true,
  hideLoader = false,
  searchPlaceholder,
  emptyMessage = "No results found.",
  maxHeight = "75",
  renderOption,
  renderValue,
  placeholderContent,
  fixedLabelWidth,
  className,
  clearLabel = "Clear selection",
}: InlineSelectProps<T>) {
  const [search, setSearch] = useState("")
  const triggerRef = useRef<HTMLDivElement>(null)

  const { value, setValue, isSubmitting } = useInlineEdit({
    value: controlledValue,
    defaultValue,
    onChange,
    onSubmit: onSave,
    ref: triggerRef,
    disableClickOutside: true,
    submitOnChange: true,
    isEmpty: (val) => val === null || val === undefined,
  })

  const [isOpen, setIsOpen] = useState(false)
  const selectedOption = options.find((opt) => opt.value === value) ?? null
  const hasSearch = !!onSearch

  const debouncedSearch = useDebounce((query: string) => {
    onSearch?.(query)
  }, 300)

  function resetSearch() {
    debouncedSearch.cancel()
    if (!hasSearch || search === "") return

    setSearch("")
    onSearch?.("")
  }

  function handleSelect(newValue: T | null) {
    setValue(newValue)
    setIsOpen(false)
    resetSearch()
  }

  function handleSearchChange(query: string) {
    setSearch(query)
    if (hasSearch) {
      debouncedSearch(query)
    }
  }

  function handleClear() {
    handleSelect(null)
  }

  function handleOpenChange(open: boolean) {
    setIsOpen(open)
    if (!open) resetSearch()
  }

  const displayValue = renderValue ? renderValue(selectedOption) : selectedOption?.label

  return (
    <BaseInlineInput
      label={label}
      LabelIcon={LabelIcon}
      hideLabel={hideLabel}
      size={size}
      fixedLabelWidth={fixedLabelWidth}
    >
      <div className="relative w-full">
        <Popover open={isOpen} onOpenChange={handleOpenChange} modal>
          <PopoverTrigger asChild>
            <div ref={triggerRef} className="w-full">
              <button
                type="button"
                className={cn(
                  "focus:outline-primary flex h-8 w-full items-center justify-between rounded-md border-1 border-transparent px-2 shadow-none focus:outline-1 md:text-sm",
                  !selectedOption && "text-input-placeholder",
                  disabled ? "cursor-default opacity-50" : "hover:bg-muted cursor-pointer",
                  className
                )}
                disabled={disabled}
              >
                {renderValue && !!renderValue(selectedOption) ? (
                  renderValue(selectedOption)
                ) : !selectedOption && placeholderContent ? (
                  placeholderContent
                ) : (
                  <span className={cn("truncate", !selectedOption && "text-input-placeholder")}>
                    {displayValue || placeholder}
                  </span>
                )}
              </button>
            </div>
          </PopoverTrigger>
          <PopoverContent className="p-0" align="start">
            <Command shouldFilter={!hasSearch}>
              {hasSearch && (
                <CommandInput
                  placeholder={searchPlaceholder || (isSearching ? "Searching..." : placeholder)}
                  value={search}
                  onValueChange={handleSearchChange}
                  icon={isSearching ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : undefined}
                />
              )}

              <CommandList className="p-0">
                <CommandEmpty className="text-muted-foreground p-2 text-center text-xs">{emptyMessage}</CommandEmpty>
                <ScrollArea className="h-full">
                  <CommandGroup className={`max-h-${maxHeight} p-1`}>
                    {clearable && value !== null && (
                      <CommandItem
                        key="__clear__"
                        value="__clear__"
                        onSelect={() => handleClear()}
                        className="text-muted-foreground cursor-pointer"
                      >
                        {clearLabel}
                      </CommandItem>
                    )}
                    {options.map((option) => (
                      <CommandItem
                        key={String(option.value)}
                        value={String(option.value)}
                        onSelect={() => handleSelect(option.value)}
                        className="cursor-pointer"
                      >
                        <div className="flex flex-1 items-center gap-2">
                          {option.icon}
                          {renderOption ? (
                            renderOption(option)
                          ) : (
                            <div className="flex flex-col">
                              <span>{option.label}</span>
                              {option.description && (
                                <span className="text-muted-foreground text-xs">{option.description}</span>
                              )}
                            </div>
                          )}
                        </div>
                        <Check className={`ml-auto h-4 w-4 ${value === option.value ? "opacity-100" : "opacity-0"}`} />
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </ScrollArea>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        {!hideLoader && (isSaving || isSubmitting) && (
          <Loader2 className="text-icon absolute top-2 right-2 h-4 w-4 animate-spin" />
        )}
      </div>
    </BaseInlineInput>
  )
}
